import { lazy, memo, Suspense } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { ClosedCaptions } from '@src/components';
import { CreateCtaModal } from '@src/features';
import { CTAManagementModal } from '@src/features/cta/partials';
import { OLoader } from '@src/shared';

import { useRoomContext } from '@src/contexts';
import { useVirtualBackgroundProducerUpdate } from '@src/hooks';
import { useCalculatorStore, useRoomActionsStore } from '@src/stores';

import 'react-toastify/dist/ReactToastify.css';
import './roomScreen.styles.scss';

const InfoModal = lazy(() => import('@src/components/InfoModal'));
const Calculator = lazy(() => import('@src/features/calculator'));
const SettingsModal = lazy(() => import('@src/components/SettingsModal'));
const VideoContainer = lazy(() => import('@src/components/VideosContainer'));
const ViewLayoutModal = lazy(() => import('@src/components/ViewLayoutModal'));
const BackgroundEffects = lazy(() => import('@src/components/BackgroundEffects'));

/**
 * RoomScreen Component for WEBINAR Calls
 *
 * This is the main room screen for Webinar calls.
 * For standard Meet room screens, see pages/MeetRoom/containers/RoomScreen/RoomScreen.tsx
 */
const RoomScreen = () => {
  const { isMobileDevice } = useRoomContext();

  // Auto-update producer when VB changes (call-time only)
  useVirtualBackgroundProducerUpdate();

  const { isTranscriptionActive } = useRoomActionsStore(
    useShallow((state) => ({
      isTranscriptionActive: state.transcriptionSettings.isActive,
    }))
  );

  const { sharedCalculatorData, isPersonalCalculatorVisible } = useCalculatorStore(
    useShallow((state) => ({
      sharedCalculatorData: state.sharedCalculatorData,
      isPersonalCalculatorVisible: state.isPersonalCalculatorVisible,
    }))
  );

  return (
    <div className="meet-room bg-main-color-500">
      <div className="main-room-container">
        <Suspense fallback={<OLoader />}>
          <VideoContainer />
        </Suspense>
        {isTranscriptionActive && !isMobileDevice && <ClosedCaptions />}
      </div>
      <Suspense fallback={<OLoader />}>
        <SettingsModal />
      </Suspense>
      <Suspense fallback={<OLoader />}>
        <ViewLayoutModal />
      </Suspense>
      {!isMobileDevice && (
        <Suspense fallback={<OLoader />}>
          <BackgroundEffects />
        </Suspense>
      )}
      <Suspense fallback={<OLoader />}>
        <InfoModal />
      </Suspense>
      {/* CTA */}
      <CreateCtaModal />
      <CTAManagementModal />

      {sharedCalculatorData.isOpen && !sharedCalculatorData.isSharedByMe && (
        <Suspense fallback={null}>
          <Calculator isReadOnly isShared />
        </Suspense>
      )}

      {(isPersonalCalculatorVisible || (sharedCalculatorData.isSharedByMe && !isMobileDevice)) && (
        <Suspense fallback={null}>
          <Calculator />
        </Suspense>
      )}
    </div>
  );
};

export default memo(RoomScreen);
